import { useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import {
  AuditEvent,
  ChangeTypeUpdateHoldMusic,
  mockSearchAuditEventsResponse,
  ObjectTypeHoldMusic,
  UpdateHoldMusicContext,
  useSearchAuditEvents,
} from '@frontend/api-phone-audits';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { Text, useDebouncedValue, useModalControl } from '@frontend/design-system';
import {
  DetailsHeader,
  LocationDetails,
} from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import { SETTING_TYPE_OPTIONS } from '../../components/phone-system-history/phone-system-history-config';
import {
  AuditEventFilters,
  defaultAuditEventFilters,
} from '../../components/phone-system-history/phone-system-history-filters';
import { PhoneSystemHistoryTable } from '../../components/phone-system-history/phone-system-history-table';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { useTenantLocationIds } from '../../utils/use-tenant-location-ids';

export function PhoneSystemHistoryIndex() {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId || '';
  const tenantLocationIds = useTenantLocationIds();

  const isUnifyType = settingsTenantLocation?.locationType === 'unify';

  const canEditUsers = tenantLocationIds.some((locationId) => hasSchemaACL(locationId, Permission.USER_WRITE));

  const { t } = useTranslation('phone');

  const detailsTray = useModalControl();

  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | undefined>(undefined);

  // ----- Filter state -----
  const [filters, setFilters] = useState<AuditEventFilters>(defaultAuditEventFilters);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue, 500);

  const handleFiltersChange = useCallback((nextFilters: AuditEventFilters) => {
    setFilters(nextFilters);
  }, []);

  const { data, isLoading } = useSearchAuditEvents(
    {
      tenantId,
      searchInput: debouncedSearch || undefined,
      objectTypes: filters.objectTypes.length ? filters.objectTypes : undefined,
      actions: filters.actions.length ? filters.actions : undefined,
      userIds: filters.userIds.length ? filters.userIds : undefined,
      timeRange:
        filters.startDate || filters.endDate ? { startTime: filters.startDate, endTime: filters.endDate } : undefined,
    },
    {
      retry: false,
      // enabled: !!tenantId,
      // TODO: Remove this once the backend is ready
      initialData: mockSearchAuditEventsResponse,
      enabled: false,
    }
  );

  return (
    <>
      <PhoneSettingsPage
        renderContainer={false}
        title={t('Phone System History')}
        subtitle={
          <span
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            {data?.trackingStartTime && (
              <Text size='medium' color='light'>
                {t('Data available starting {{date}}', {
                  date: formatDate(data.trackingStartTime, 'MMMM DD, YYYY h:mm A'),
                })}
              </Text>
            )}
            <Text>
              {t(
                'Track changes by name (or Object ID), action (update, creation, and deletion), by date, or by user who updated the system.'
              )}
            </Text>
          </span>
        }
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Phone System History'), to: '/phone/phone-system-history' },
        ]}
        css={css`
          header {
            div:first-of-type {
              gap: 0;
            }
          }
          // subtract the width of the tray from the width of the page when the tray is open, so that the page content
          // doesn't get hidden behind it
          width: ${detailsTray.modalProps.show ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
          transition: width 0.3s ease;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <PhoneSystemHistoryAvailabilityBanner />

          <PhoneSystemHistoryTable
            data={data}
            isLoading={isLoading}
            canEditUsers={canEditUsers}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onDetailsClick={(_event) => {
              setSelectedEvent(_event);
              detailsTray.openModal();
            }}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isTrayOpen={detailsTray.modalProps.show}
            selectedEventId={selectedEvent?.id}
          />
        </div>
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={detailsTray}
        tenantId={settingsTenantLocation?.phoneTenantId ?? ''}
        callObjectId={selectedEvent?.objectId}
        callObjectType={selectedEvent?.objectType}
        auditEventId={selectedEvent?.id}
        HeaderComponent={
          <DetailsHeader
            objectType={selectedEvent?.objectType}
            onClose={() => {
              detailsTray.closeModal();
              setSelectedEvent(undefined);
            }}
          >
            {isUnifyType &&
              selectedEvent?.objectType === ObjectTypeHoldMusic &&
              selectedEvent?.changes?.[0]?.type === ChangeTypeUpdateHoldMusic && (
                <LocationDetails
                  locationNames={[
                    (selectedEvent.changes?.[0] as { updateHoldMusic: UpdateHoldMusicContext })?.updateHoldMusic
                      ?.locations?.[0]?.locationName ?? t('Unknown Location'),
                  ]}
                />
              )}
          </DetailsHeader>
        }
      />
    </>
  );
}

function PhoneSystemHistoryAvailabilityBanner() {
  const { t } = useTranslation('phone');

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border: 1px solid var(--fabric-color-border-default);
        border-radius: 8px;
        background-color: var(--fabric-color-surface-inset-default);
        max-width: 700px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: var(--fabric-color-surface-inset-hover);
        `}
      >
        <Icon
          name='history-small'
          color='subdued'
          css={css`
            flex-shrink: 0;
            width: 24px;
          `}
        />
      </div>
      <div>
        <Text weight='bold'>{t('Currently Available:')}</Text>
        <Text size='medium' color='light'>
          {SETTING_TYPE_OPTIONS.map(({ label }) => label).join(', ')}
        </Text>
      </div>
    </div>
  );
}
